import { Box, Button, ButtonGroup, Checkbox, Dialog, Fab, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TableContainer } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { loadCodes, loadTypes } from "../companySettings/companySettingsApi"
import { DefaultCompanySetting, loadDefaultCompanySettings } from "./defaultCompanySettingsApi"
import { CreateEditCompanySettingsDialog } from "./CreateEditCompanySettingsDialog"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import SyncIcon from '@mui/icons-material/Sync';
import { RegenerateCompanySettingsDialog } from "./RegenerateCompanySettingsDialog"
import { GenerateCodeDefaultCompanySettingsDialog } from "./GenerateCodeDefaultCompanySettingsDialog"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"

type DialogState =
    | { type: "none" }
    | { type: "createEdit", companySetting: DefaultCompanySetting | undefined }
    | { type: "generateCode", companySettings: DefaultCompanySetting[] | undefined }
    | { type: "regenerateSettings" }

export const DefaultCompanySettings = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [companySettings, setCompanySettings] = useState<DefaultCompanySetting[]>([]);
    const [filteredCompanySettings, setFilteredCompanySettings] = useState<DefaultCompanySetting[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [types, setTypes] = useState<string[]>([]);
    const [codes, setCodes] = useState<string[]>([]);
    const [triggerRefresh, setTriggerRefresh] = useState(false);

    const [type, setType] = useState('all');
    const [code, setCode] = useState('all');

    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        if(triggerRefresh) {
            handleRefreshClick();
            setTriggerRefresh(false);
        }
    }, [companySettings]);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadDefaultCompanySettings())
            .then(unwrapResult)
            .then(result => {
                setCompanySettings(result);
                setFilteredCompanySettings(result);
            })
            .catch((error) => console.error(error))
            .finally(() => setIsLoading(false));
        dispatch(loadTypes())
            .then(unwrapResult)
            .then(setTypes)
            .catch((error) => console.error(error))
        dispatch(loadCodes())
            .then(unwrapResult)
            .then(setCodes)
            .catch((error) => console.error(error))
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((systemSetting: DefaultCompanySetting | DefaultCompanySetting[]) => {
            setter({ type: "none" })
            setCompanySettings(
                produce((draft) => {
                    if (draft !== undefined) {
                        if(Array.isArray(systemSetting)) {
                            draft.push(...systemSetting);
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === systemSetting.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = systemSetting;
                            } else {
                                draft.push(systemSetting);
                            }
                        }
                    }
                })
            )
            setTriggerRefresh(true);
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "createEdit":
                return <CreateEditCompanySettingsDialog
                    mode={state.companySetting ? "edit" : "create"}
                    companySetting={state.companySetting}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
            case "generateCode":
                return <GenerateCodeDefaultCompanySettingsDialog
                    companySettings={state.companySettings}
                    onCancel={onCancel}
                />

            case "regenerateSettings":
                return <RegenerateCompanySettingsDialog
                    onCancel={onCancel}
                />
        }
    }

    const handleTypeChange = (event) => {
        setType(event.target.value);
    };

    const handleCodeChange = (event) => {
        setCode(event.target.value);
    };

    const handleAddSystemSettingClick = useCallback(() => {
        setDialog({
            type: "createEdit",
            companySetting: undefined
        })
    }, [setDialog])

    const handleEditSystemSettingClick = useCallback((companySetting: DefaultCompanySetting) => {
        setDialog({
            type: "createEdit",
            companySetting
        })
    }, [setDialog])

    const handleGenerateCodeClick = useCallback(() => {
        setDialog({
            type: "generateCode",
            companySettings
        })
    }, [setDialog, companySettings])

     const handleRegenerateSettingsClick = useCallback(() => {
        setDialog({
            type: "regenerateSettings"
        })
    }, [setDialog, companySettings])

    const handleRefreshClick = () => {
        const filter = companySettings.filter(item => {
            const typeMatch = type === "all" || item.type === type;
            const codeMatch = code === "all" || item.code === code;

            return typeMatch && codeMatch;
        });

        setFilteredCompanySettings(filter);
    }

    const columns: GridColDef<DefaultCompanySetting, any, any>[] = [
        {
            field: 'type',
            headerName: l10n.getString("type", null, "_Typ"),
            flex: 1
        },
        {
            field: 'code',
            headerName: l10n.getString("system-settings-code", null, "_Kod"),
            flex: 1,
        },
        {
            field: 'value',
            headerName: l10n.getString("system-settings-value", null, "_Wartość"),
            flex: 1,
        },
        {
            field: 'isActive',
            headerName: l10n.getString("system-settings-active", null, "_Aktywne"),
            flex: 1,
            renderCell: (params) => <Checkbox disabled checked={params.row.isActive}/>
        },
        {
            field: 'isFixed',
            headerName: l10n.getString("system-settings-fixed", null, "_Stałe"),
            flex: 1,
            renderCell: (params) => <Checkbox disabled checked={params.row.isFixed}/>
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            flex: 1,
            valueGetter: (_, row) => row.createDate ? new Date(row.createDate).toLocaleString() : null
        },
        {
            field: 'modifiedBy',
            headerName: l10n.getString("modified-by", null, "_Modifikowane przez"),
            flex: 1
        },
        {
            field: 'modifyDate',
            headerName: l10n.getString("modify-date", null, "_Data modyfikacji"),
            flex: 1,
            valueGetter: (_, row) => row.modifyDate ? new Date(row.modifyDate).toLocaleString() : null
        },
        {
            field: 'actions',
            type: 'actions',
            width: 250,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleEditSystemSettingClick(params.row)}
                        >
                            <Localized id="edit">
                                <span>_Edytuj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <div>
                <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                    <FormControl>
                        <InputLabel id="select-type-label"><Localized id="type">_Typ</Localized></InputLabel>
                        <Select
                            labelId="select-type-label"
                            value={type}
                            onChange={handleTypeChange}
                            label={<Localized id="type">_Typ</Localized>}
                            sx={{ width: '200px' }}
                        >
                            <MenuItem value={'all'}>
                                <Localized id="all">All</Localized>
                            </MenuItem>
                            {types.map((option) =>
                                <MenuItem value={option}>{option}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel id="select-code-label"><Localized id="system-settings-code">_Kod</Localized></InputLabel>
                        <Select
                            labelId="select-code-label"
                            value={code}
                            onChange={handleCodeChange}
                            label={<Localized id="system-settings-code">_Kod</Localized>}
                            sx={{ width: '200px' }}
                        >
                            <MenuItem value={'all'}>
                                <Localized id="all">All</Localized>
                            </MenuItem>
                            {codes.map((option) =>
                                <MenuItem value={option}>{option}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <IconButton onClick={handleRefreshClick}>
                        <SyncIcon />
                    </IconButton>
                </Stack>
            </div>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 350px)' }}
                    rows={filteredCompanySettings}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                    onClick={() => handleAddSystemSettingClick()} color="secondary" size="small" >
                        <AddIcon />
                    </Fab>
                    <Button sx={{ ml: 2 }} onClick={() => handleGenerateCodeClick()}>
                        <Localized id="default-company-settings-generate-script">
                            <span>_Wygeneruj skrypt</span>
                        </Localized>
                    </Button>
                    <Button sx={{ ml: 2 }} onClick={() => handleRegenerateSettingsClick()}>
                        <Localized id="default-company-settings-regenerate-settings">
                            <span>_Przegeneruj ustawienia dla firm</span>
                        </Localized>
                    </Button>
                </Box>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth={dialog.type === "generateCode" ? "xl" : "sm"} keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
